import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatInTimeZone } from 'date-fns-tz';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';
import { 
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useData } from '../contexts/DataContext';
import { BatteryControlSchedule } from '../types/api';
import BatteryControlSchedulesTile from '../components/BatteryControlSchedulesTile';

const DashboardPage: React.FC = () => {
  const { selectedDataId, setSelectedDataId, isNewDataAvailable, shouldAutoSelectLatest, setShouldAutoSelectLatest } = useData();
  const queryClient = useQueryClient();
  const [refreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [chartHistoryHours, setChartHistoryHours] = useState(12);
  const [chartTooltipTimeout, setChartTooltipTimeout] = useState<NodeJS.Timeout | null>(null);

  const [dataEntries, setDataEntries] = useState<any[]>([]);
  const [seriesVisibility, setSeriesVisibility] = useState({
    toConsumer: true,
    fromBattToConsumer: true,
    fromGenToConsumer: true,
    fromGridToConsumer: true,
    energyGridPlus: true,
    energyGridMinus: true,
    batteryStorage: true,
    toBatt: true,
    fromBatt: true,
    fromBattToGrid: true,
    fromGen: true,
    generalPrice: true,
    feedInPrice: true
  });

  // Performance monitoring
  const [performanceMetrics, setPerformanceMetrics] = useState<{
    pageLoadStart: number;
    dataLoadComplete: number | null;
    chartRenderComplete: number | null;
  }>({
    pageLoadStart: Date.now(),
    dataLoadComplete: null,
    chartRenderComplete: null
  });

  // Get user's timezone from browser
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Map timezone to locale
  const getLocaleFromTimezone = (timezone: string) => {
    if (timezone.includes('Australia')) return 'en-AU';
    if (timezone.includes('US') || timezone.includes('America')) return 'en-US';
    if (timezone.includes('Europe')) return 'en-GB';
    return 'en-AU'; // default
  };

  const userLocale = getLocaleFromTimezone(userTimezone);

  const { isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['serviceStatus'],
    queryFn: () => apiService.getServiceStatus(),
    refetchInterval: 300000,
  });

  const { data: chartData, isLoading: chartLoading, refetch: refetchChart, error: chartError } = useQuery({
    queryKey: ['chartData', selectedDataId, refreshKey],
    queryFn: () =>
      selectedDataId ? apiService.getChartData(selectedDataId) : apiService.getLatestChartData(),
    refetchInterval: 60000,
    enabled: !!selectedDataId || dataEntries.length > 0,
  });

  const { data: dataEntriesData, isLoading: dataEntriesLoading, refetch: refetchDataEntries } = useQuery({
    queryKey: ['dataEntries'],
    queryFn: () => apiService.getUserDataEntries(50),
    refetchInterval: 300000,
  });

  const { data: batteryControlSchedules, isLoading: batterySchedulesLoading, refetch: refetchBatterySchedules } = useQuery({
    queryKey: ['batteryControlSchedules'],
    queryFn: () => apiService.getBatteryControlSchedules(),
    refetchInterval: 300000,
  });

  const { data: batteryRecommendations, isLoading: batteryRecommendationsLoading, refetch: refetchBatteryRecommendations } = useQuery({
    queryKey: ['batteryControlScheduleRecommendations'],
    queryFn: () => apiService.getBatteryControlScheduleRecommendations(false),
    refetchInterval: 300000,
  });

  const { data: gridExportStatus, refetch: refetchGridExport } = useQuery({
    queryKey: ['gridExportStatus'],
    queryFn: () => apiService.getGridExportStatus(),
    refetchInterval: 300000,
  });

  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiService.getSettings(),
    refetchInterval: 300000,
  });

  useEffect(() => {
    if (chartError) {
      const error = chartError as any;
      console.error('Error loading chart data:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to load chart data';
      toast.error(errorMsg);
    }
  }, [chartError]);

  useEffect(() => {
    if (dataEntriesData?.entries && dataEntriesData.entries.length > 0 && selectedDataId === null) {
      setSelectedDataId(dataEntriesData.entries[0].id);
    }
  }, [dataEntriesData, selectedDataId, setSelectedDataId]);

  useEffect(() => {
    if (settingsData?.settings?.CHART_HISTORY_HOURS) {
      setChartHistoryHours(settingsData.settings.CHART_HISTORY_HOURS);
    }
  }, [settingsData]);

  // Update data entries when data is loaded
  useEffect(() => {
    if (dataEntriesData?.entries) {
      setDataEntries(dataEntriesData.entries);
      
      // Track when all data is loaded
      if (dataEntriesData.entries.length > 0 && !performanceMetrics.dataLoadComplete) {
        setPerformanceMetrics(prev => ({
          ...prev,
          dataLoadComplete: Date.now()
        }));
        
        const loadTime = Date.now() - performanceMetrics.pageLoadStart;
        console.log(`🚀 Dashboard data loaded in ${loadTime}ms`);
      }

      // Auto-select the latest entry if no data is currently selected (initial load)
      if (!selectedDataId && dataEntriesData.entries.length > 0) {
        const latestEntry = dataEntriesData.entries[0];
        console.log(`🔄 Auto-selecting latest data entry for initial load: ${latestEntry.id}`);
        setSelectedDataId(latestEntry.id);
      }
    }
      }, [dataEntriesData, performanceMetrics.pageLoadStart, performanceMetrics.dataLoadComplete, selectedDataId, setSelectedDataId]);

  // Reset performance metrics when selectedDataId changes (new date selected)
  useEffect(() => {
    setPerformanceMetrics({
      pageLoadStart: Date.now(),
      dataLoadComplete: null,
      chartRenderComplete: null
    });
  }, [selectedDataId]);



  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Call APIs with refresh=true, then refetch queries
      await Promise.all([
        apiService.getBatteryControlSchedules(true),
        apiService.getBatteryControlScheduleRecommendations(true)
      ]);
      
      // Refetch all data including data entries
      const [, , dataEntriesResult] = await Promise.all([
        refetchStatus(),
        refetchChart(),
        refetchDataEntries(),
        refetchBatterySchedules(),
        refetchBatteryRecommendations()
      ]);
      
      // After refresh, handle auto-selection based on context
      if (shouldAutoSelectLatest) {
        // If we should auto-select latest (e.g., after sync completion), use the fresh data entries
        if (dataEntriesResult.data?.entries && dataEntriesResult.data.entries.length > 0) {
          const latestEntry = dataEntriesResult.data.entries[0];
          console.log(`🔄 Auto-selecting latest data entry after sync completion: ${latestEntry.id}`);
          setSelectedDataId(latestEntry.id);
          setShouldAutoSelectLatest(false); // Reset the flag
        }
      } else {
        // For manual refresh, select the latest data entry if available
        if (dataEntriesData?.entries && dataEntriesData.entries.length > 0) {
          const latestEntry = dataEntriesData.entries[0];
          console.log(`🔄 Manual refresh - selecting latest data entry: ${latestEntry.id}`);
          setSelectedDataId(latestEntry.id);
        }
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [shouldAutoSelectLatest, dataEntriesData?.entries, refetchStatus, refetchChart, refetchDataEntries, refetchBatterySchedules, refetchBatteryRecommendations, setSelectedDataId, setShouldAutoSelectLatest]);

  // Show notification when new data is available
  useEffect(() => {
    if (isNewDataAvailable) {
      // You could show a toast notification here instead of console.log
      console.log('🆕 New data is available and will auto-refresh shortly...');
    }
  }, [isNewDataAvailable]);

  // Listen for data refresh requests from the DataContext
  useEffect(() => {
    const handleDataRefresh = () => {
      console.log('🔄 Data refresh requested, refetching all data...');
      handleRefresh();
    };

    window.addEventListener('dataRefreshRequested', handleDataRefresh);
    return () => window.removeEventListener('dataRefreshRequested', handleDataRefresh);
  }, [handleRefresh]);

  // Listen for forecast updates from WebSocket - only refresh Battery Status chart
  useEffect(() => {
    const handleForecastUpdate = async () => {
      console.log('📊 Forecast updated, refreshing Battery Status chart...');
      // Invalidate and immediately refetch chartData query to update Battery Status chart
      // This bypasses cache and forces immediate refresh
      await queryClient.invalidateQueries({ queryKey: ['chartData'] });
      await queryClient.refetchQueries({ queryKey: ['chartData'], type: 'active' });
    };

    window.addEventListener('forecastUpdated', handleForecastUpdate);
    
    return () => {
      window.removeEventListener('forecastUpdated', handleForecastUpdate);
    };
  }, [queryClient]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (chartTooltipTimeout) {
        clearTimeout(chartTooltipTimeout);
      }
    };
  }, [chartTooltipTimeout]);

  const handlePreviousDataEntry = () => {
    if (dataEntries.length > 0) {
      const currentIndex = dataEntries.findIndex(entry => entry.id === selectedDataId);
      if (currentIndex < dataEntries.length - 1) {
        setSelectedDataId(dataEntries[currentIndex + 1].id);
      }
    }
  };

  const handleNextDataEntry = () => {
    if (dataEntries.length > 0) {
      const currentIndex = dataEntries.findIndex(entry => entry.id === selectedDataId);
      if (currentIndex > 0) {
        setSelectedDataId(dataEntries[currentIndex - 1].id);
      }
    }
  };

  const selectedEntry = dataEntries.find(entry => entry.id === selectedDataId);

  const toggleSeries = (seriesKey: string) => {
    setSeriesVisibility(prev => ({
      ...prev,
      [seriesKey]: !prev[seriesKey as keyof typeof prev]
    }));
  };

  const handleChartHistoryChange = async (hours: number) => {
    try {
      // Update the setting on the backend
      await apiService.updateSetting('CHART_HISTORY_HOURS', { value: hours });
      
      // Update local state
      setChartHistoryHours(hours);
      
      console.log(`📊 Chart history updated to ${hours} hours`);
    } catch (error) {
      console.error('Error updating chart history setting:', error);
    }
  };



  // Handle chart touch interactions for mobile tooltip auto-hide
  const handleChartTouch = () => {
    // Clear existing timeout
    if (chartTooltipTimeout) {
      clearTimeout(chartTooltipTimeout);
    }
    
    // Reset any previously hidden tooltips to make them visible again
    const tooltipWrappers = document.querySelectorAll('.recharts-tooltip-wrapper');
    tooltipWrappers.forEach(wrapper => {
      if (wrapper instanceof HTMLElement) {
        wrapper.style.display = '';
      }
    });
    
    // Set timeout to hide tooltip after 2 seconds
    const timeout = setTimeout(() => {
      // Find and hide all visible tooltips
      const tooltipWrappers = document.querySelectorAll('.recharts-tooltip-wrapper');
      tooltipWrappers.forEach(wrapper => {
        if (wrapper instanceof HTMLElement) {
          wrapper.style.display = 'none';
        }
      });
    }, 2000);
    
    setChartTooltipTimeout(timeout);
  };

  // Custom tooltip component with auto-hide for mobile
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white text-gray-900 p-4 rounded-lg shadow-xl border-2 border-gray-200 min-w-[200px]">
          <p className="text-sm font-semibold mb-3 pb-2 border-b border-gray-200 text-gray-800">
            {format(parseISO(label), 'MMM dd, yyyy HH:mm')}
          </p>
          <div className="space-y-1.5">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex items-center justify-between gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-sm flex-shrink-0" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="font-medium text-gray-700">{entry.name}:</span>
                </div>
                <span className="font-semibold text-gray-900 tabular-nums">
                  {typeof entry.value === 'number' ? 
                    (entry.name.includes('Price') ? `${entry.value.toFixed(2)} ¢/kWh` : 
                     entry.name.includes('Storage') ? `${entry.value.toFixed(2)} kWh` : 
                     `${entry.value.toFixed(2)} kW`) : 
                    entry.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Helper to ensure correct shape
  function ensureChartData<T>(result: any): { chartData: T[]; refLineTimestamp: string | undefined; lowPeriods?: any[]; feedinPeriods?: any[] } {
    if (result && Array.isArray(result.chartData)) return result;
    return { chartData: [], refLineTimestamp: undefined, lowPeriods: [], feedinPeriods: [] };
  }

  // Get the reference line timestamp - use data time when Data Time is selected, otherwise use current time
  const getRefLineTimestamp = () => {
    // If we have a selected data entry, use its timestamp
    if (currentDataEntry?.created_at) {
      return currentDataEntry.created_at;
    }
    
    // Otherwise use current time as fallback
    return new Date().toISOString();
  };

  // Find the closest time point in chart data for reference line positioning
  const findClosestTimePoint = (chartData: any[], targetTime: string) => {
    if (!chartData || chartData.length === 0 || !targetTime) return null;
    
    const targetDate = new Date(targetTime);
    let closestPoint = chartData[0];
    let minDiff = Math.abs(new Date(chartData[0].time).getTime() - targetDate.getTime());
    
    for (let i = 1; i < chartData.length; i++) {
      const diff = Math.abs(new Date(chartData[i].time).getTime() - targetDate.getTime());
      if (diff < minDiff) {
        minDiff = diff;
        closestPoint = chartData[i];
      }
    }
    
    return closestPoint;
  };



  const getCurrentDataEntry = () => {
    if (!selectedDataId || !dataEntries.length) return null;
    return dataEntries.find(entry => entry.id === selectedDataId);
  };

  const currentDataEntry = getCurrentDataEntry();

  // Calculate reference line timestamp once
  const refLineTimestamp = getRefLineTimestamp();
  


  // Helper function to filter chart data based on chart history setting
  const filterChartDataByHistory = (data: any[], dataTime: string, historyHours: number) => {
    if (!data || data.length === 0 || !dataTime || historyHours <= 0) {
      return data;
    }

    const dataTimeDate = new Date(dataTime);
    const cutoffTime = new Date(dataTimeDate.getTime() - (historyHours * 60 * 60 * 1000));

    return data.filter(point => {
      const pointTime = new Date(point.time);
      return pointTime >= cutoffTime;
    });
  };

  // Prepare data for charts from the chart data API
  const consumptionChartData = ensureChartData(chartData?.consumption ? (() => {
    const chartDataPoints = chartData.consumption.map((point: any, index: number) => ({
      id: `consumption-${index}`,
      time: point.time, // Keep original time format
      fromBattToConsumer: point.fromBattToConsumer,
      fromGenToConsumer: point.fromGenToConsumer,
      fromGridToConsumer: point.fromGridToConsumer,
      toConsumer: point.toConsumer
    }));
    
    // Filter data based on chart history setting
    const filteredData = filterChartDataByHistory(chartDataPoints, refLineTimestamp, chartHistoryHours);
    
    const closestPoint = findClosestTimePoint(filteredData, refLineTimestamp);
    
    return { 
      chartData: filteredData, 
      refLineTimestamp: closestPoint?.time || refLineTimestamp, 
      lowPeriods: [], 
      feedinPeriods: [] 
    };
  })() : { chartData: [], refLineTimestamp, lowPeriods: [], feedinPeriods: [] });

  const generationChartData = ensureChartData(chartData?.generation ? (() => {
    const chartDataPoints = chartData.generation.map((point: any, index: number) => ({
      id: `generation-${index}`,
      time: point.time, // Keep original time format
      fromGen: point.fromGen,
      energyGridPlus: point.energyGridPlus,
      energyGridMinus: point.energyGridMinus
    }));
    
    // Filter data based on chart history setting
    const filteredData = filterChartDataByHistory(chartDataPoints, refLineTimestamp, chartHistoryHours);
    
    const closestPoint = findClosestTimePoint(filteredData, refLineTimestamp);
    
    return { 
      chartData: filteredData, 
      refLineTimestamp: closestPoint?.time || refLineTimestamp, 
      lowPeriods: [], 
      feedinPeriods: [] 
    };
  })() : { chartData: [], refLineTimestamp, lowPeriods: [], feedinPeriods: [] });

  const batteryChartData = ensureChartData(chartData?.battery ? (() => {
    const chartDataPoints = chartData.battery.map((point: any, index: number) => ({
      id: `battery-${index}`,
      time: point.time, // Keep original time format
      toBatt: point.toBatt,
      fromBatt: point.fromBatt,
      fromBattToGrid: point.fromBattToGrid,
      batteryStorage: point.batteryStorage
    }));
    
    // Filter data based on chart history setting
    const filteredData = filterChartDataByHistory(chartDataPoints, refLineTimestamp, chartHistoryHours);
    
    const closestPoint = findClosestTimePoint(filteredData, refLineTimestamp);
    
    return { 
      chartData: filteredData, 
      refLineTimestamp: closestPoint?.time || refLineTimestamp, 
      lowPeriods: [], 
      feedinPeriods: [] 
    };
  })() : { chartData: [], refLineTimestamp, lowPeriods: [], feedinPeriods: [] });

  const pricingChartData = ensureChartData(chartData?.pricing?.chartData ? (() => {
    const chartDataPoints = chartData.pricing.chartData.map((point: any, index: number) => ({
      id: `pricing-${index}`,
      time: point.time, // Keep original time format
      generalPrice: point.generalPrice,
      feedInPrice: point.feedInPrice
    }));
    
    // Filter data based on chart history setting
    const filteredData = filterChartDataByHistory(chartDataPoints, refLineTimestamp, chartHistoryHours);
    
    const closestPoint = findClosestTimePoint(filteredData, refLineTimestamp);
    
    return { 
      chartData: filteredData, 
      refLineTimestamp: closestPoint?.time || refLineTimestamp, 
      lowPeriods: chartData.pricing.lowPeriods || [], 
      feedinPeriods: chartData.pricing.feedinPeriods || [] 
    };
  })() : { chartData: [], refLineTimestamp, lowPeriods: [], feedinPeriods: [] });

  const [chartWidth, setChartWidth] = useState(0);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.offsetWidth);
      }
    };
    
    // Use ResizeObserver to detect when chart containers are rendered
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
      
      // Track when charts are fully rendered
      if (!performanceMetrics.chartRenderComplete) {
        setPerformanceMetrics(prev => ({
          ...prev,
          chartRenderComplete: Date.now()
        }));
        
        const renderTime = Date.now() - performanceMetrics.pageLoadStart;
        console.log(`📊 Charts rendered in ${renderTime}ms`);
      }
    });
    
    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }
    
    // Also listen for window resize
    window.addEventListener('resize', updateWidth);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [batteryChartData.chartData, pricingChartData.chartData, generationChartData.chartData, consumptionChartData.chartData, performanceMetrics.pageLoadStart, performanceMetrics.chartRenderComplete, chartHistoryHours]);

  // Calculate interval based on width
  const getInterval = (width: number) => {
    if (width < 400) return 53;      // Small screens: fewer ticks
    if (width < 800) return 35;      // Medium screens: moderate ticks
    return 23;                       // Large screens: more ticks
  };

  if (statusLoading || chartLoading || dataEntriesLoading || settingsLoading) {
    return <LoadingSpinner />;
  }

  if (chartError) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 space-y-4 sm:space-y-0">
              
              {/* Data Entry Navigation */}
              <div className="flex items-center space-x-2">
                <label htmlFor="data-entry-select" className="text-sm font-medium text-gray-700">
                  Data Time:
                </label>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={handlePreviousDataEntry}
                    disabled={dataEntriesLoading || dataEntries.length === 0 || selectedDataId === null || dataEntries.findIndex(entry => entry.id === selectedDataId) === dataEntries.length - 1}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed transition-colors"
                    title="Previous data entry"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <select
                    id="data-entry-select"
                    value={selectedDataId || ''}
                    onChange={(e) => setSelectedDataId(Number(e.target.value))}
                    className="block w-48 px-3 py-2 border border-gray-350 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                    disabled={dataEntriesLoading}
                  >
                    {dataEntriesLoading ? (
                      <option>Loading...</option>
                    ) : dataEntries.length > 0 ? (
                      dataEntries.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {formatInTimeZone(parseISO(entry.created_at), Intl.DateTimeFormat().resolvedOptions().timeZone, 'MMM dd, yyyy HH:mm')} 
                        </option>
                      ))
                    ) : (
                      <option>No data available</option>
                    )}
                  </select>
                  <button
                    onClick={handleNextDataEntry}
                    disabled={dataEntriesLoading || dataEntries.length === 0 || selectedDataId === null || dataEntries.findIndex(entry => entry.id === selectedDataId) === 0}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed transition-colors"
                    title="Next data entry"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Error Content */}
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-2xl mx-auto px-4">
            <AlertCircle className="h-8 w-8 mx-auto mb-4 text-red-500" />
            <p className="text-red-600 text-lg font-medium mb-2">Error loading chart data</p>
            {chartError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                <p className="text-red-800 text-sm">
                  {(chartError as any)?.response?.data?.detail || chartError?.message || 'Unknown error occurred'}
                </p>
              </div>
            )}
            <button 
              onClick={handleRefresh}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-2">
         {/* Data Entry Navigation */}
          <div className="flex items-center space-x-2">
            <label htmlFor="data-entry-select" className="text-sm font-medium text-gray-700">
              Data Time:
            </label>
            <div className="flex items-center space-x-1">
              <button
                onClick={handlePreviousDataEntry}
                disabled={dataEntriesLoading || dataEntries.length === 0 || selectedDataId === null || dataEntries.findIndex(entry => entry.id === selectedDataId) === dataEntries.length - 1}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed transition-colors"
                title="Previous data entry"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <select
                id="data-entry-select"
                value={selectedDataId || ''}
                onChange={(e) => setSelectedDataId(Number(e.target.value))}
                className="block w-48 px-3 py-2 border border-gray-350 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                disabled={dataEntriesLoading}
              >
                {dataEntriesLoading ? (
                  <option>Loading...</option>
                ) : dataEntries.length > 0 ? (
                  dataEntries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {formatInTimeZone(parseISO(entry.created_at), Intl.DateTimeFormat().resolvedOptions().timeZone, 'MMM dd, yyyy HH:mm')} 
                    </option>
                  ))
                ) : (
                  <option>No data available</option>
                )}
              </select>
              <button
                onClick={handleNextDataEntry}
                disabled={dataEntriesLoading || dataEntries.length === 0 || selectedDataId === null || dataEntries.findIndex(entry => entry.id === selectedDataId) === 0}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed transition-colors"
                title="Next data entry"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Chart History Buttons - Full version on wide screens */}
            <div className="hidden lg:flex items-center space-x-2 ml-4">
              <label className="text-sm font-medium text-gray-700">
                Chart History:
              </label>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleChartHistoryChange(4)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    chartHistoryHours === 4
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                  title="Show 4 hours of chart data"
                >
                  4h
                </button>
                <button
                  onClick={() => handleChartHistoryChange(24)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    chartHistoryHours === 24
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                  title="Show 24 hours of chart data"
                >
                  24h
                </button>
              </div>
            </div>

            {/* Chart History Toggle - Compact version on narrow screens */}
            <div className="flex lg:hidden items-center ml-4">
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleChartHistoryChange(4)}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    chartHistoryHours === 4
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                  title="Show 4 hours of chart data"
                >
                  4h
                </button>
                <button
                  onClick={() => handleChartHistoryChange(24)}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    chartHistoryHours === 24
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                  title="Show 24 hours of chart data"
                >
                  24h
                </button>
              </div>
            </div>

            {/* Show indicator when latest entry is selected */}
            {/* {dataEntries.length > 0 && selectedDataId === dataEntries[0].id && (
              <div className="flex items-center space-x-1 text-green-600 mr-16">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                <span className="text-xs">Latest</span>
              </div>
            )} */}
          </div>

        </div>
        
        {/* Auto-refresh Controls */}
        {process.env.NODE_ENV === 'development' && (
          <div className="flex items-center space-x-2">
            {/* New Data Indicator */}
            {isNewDataAvailable && (
              <div className="flex items-center space-x-2 px-2 py-2 bg-green-100 text-green-800 rounded-md text-xs font-medium">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>New data available</span>
              </div>
            )}


            {/* Manual Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center justify-center space-x-2 px-2 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              title="Refresh data"
            >
                {isRefreshing ? (
                  <span className="animate-spin h-3 w-3 border-b-2 border-gray-600 rounded-full inline-block"></span>
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
        </div>
        )}
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-5 gap-6 items-start">
        <div className="grid grid-cols-1 2xl:col-span-4 gap-6">

          {/* Battery Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-1 lg:grid-cols-1ap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 pb-0">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Battery Status</h2>          

              {/* Legend */}
              <div className="flex flex-wrap gap-2 mb-">
                <button
                  onClick={() => toggleSeries('batteryStorage')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.batteryStorage 
                      ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.batteryStorage ? '#8b5cf6' : '#d1d5db' }}></div>
                  <span>Storage</span>
                </button>
                <button
                  onClick={() => toggleSeries('toBatt')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.toBatt 
                      ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.toBatt ? '#10b981' : '#d1d5db' }}></div>
                  <span>Charging</span>
                </button>
                <button
                  onClick={() => toggleSeries('fromBatt')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.fromBatt 
                      ? 'bg-red-100 text-red-800 hover:bg-red-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.fromBatt ? '#ef4444' : '#d1d5db' }}></div>
                  <span>Discharge</span>
                </button>
                <button
                  onClick={() => toggleSeries('fromBattToGrid')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.fromBattToGrid 
                      ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.fromBattToGrid ? '#f97316' : '#d1d5db' }}></div>
                  <span>To Grid</span>
                </button>
              </div>

              {batteryChartData.chartData && batteryChartData.chartData.length > 0 ? (
                <div ref={chartRef} onTouchStart={handleChartTouch}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={batteryChartData.chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="time" 
                        height={60}
                        interval={getInterval(chartWidth)}
                        tick={(props) => {
                          const { x, y, payload } = props;
                          const date = new Date(payload.value);
                          
                          // Format the date and time
                          const timeStr = date.toLocaleTimeString(userLocale, {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          });
                          const dateStr = date.toLocaleDateString(userLocale, {
                            day: 'numeric',
                            month: 'numeric',
                          });

                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={14}>
                              {timeStr}
                            </text>
                            <text x={0} y={0} dy={32} textAnchor="middle" fill="#666" fontSize={14}>
                              {dateStr}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis tick={{ fontSize: 14 }} domain={[0, 'auto']} width={25} />
                    <Tooltip 
                      content={<CustomTooltip />}
                      cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '5 5' }}
                      animationDuration={150}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    {seriesVisibility.batteryStorage && (
                      <Line type="monotone" dataKey="batteryStorage" stroke="#8b5cf6" strokeWidth={2} name="Battery Storage" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.toBatt && (
                      <Line type="monotone" dataKey="toBatt" stroke="#10b981" strokeWidth={2} name="Battery Charging" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.fromBatt && (
                      <Line type="monotone" dataKey="fromBatt" stroke="#ef4444" strokeWidth={2} name="Battery Discharging" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.fromBattToGrid && (
                      <Line type="monotone" dataKey="fromBattToGrid" stroke="#f97316" strokeWidth={2} name="Battery to Grid" dot={false} animationDuration={0} />
                    )}
                    {batteryChartData.refLineTimestamp && batteryChartData.chartData.length > 0 && (
                      <ReferenceLine 
                        x={batteryChartData.refLineTimestamp} 
                        stroke="#DC2626" 
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        label={{
                          value: "Data Time",
                          position: "insideTopRight",
                          fill: "#DC2626",
                          fontSize: 14,
                          dy: 10
                        }}
                      />
                                         )}

                   </LineChart>
                 </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-center">
                  <p className="text-gray-500">No battery data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Pricing Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-1 lg:grid-cols-1ap-6">
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Amber Pricing</h2>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => toggleSeries('generalPrice')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.generalPrice 
                      ? 'bg-red-100 text-red-800 hover:bg-red-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.generalPrice ? '#ef4444' : '#d1d5db' }}></div>
                  <span>General Price</span>
                </button>
                <button
                  onClick={() => toggleSeries('feedInPrice')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.feedInPrice 
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.feedInPrice ? '#eab308' : '#d1d5db' }}></div>
                  <span>Feed-in Tariff</span>
                </button>
              </div>
                {pricingChartData.chartData && pricingChartData.chartData.length > 0 ? (
                <div ref={chartRef} onTouchStart={handleChartTouch}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={pricingChartData.chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="time" 
                        height={60}
                        interval={getInterval(chartWidth)*2}
                        tick={(props) => {
                          const { x, y, payload } = props;
                          const date = new Date(payload.value);
                          
                          // Format the date and time
                          const timeStr = date.toLocaleTimeString(userLocale, {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          });
                          const dateStr = date.toLocaleDateString(userLocale, {
                            day: 'numeric',
                            month: 'numeric',
                          });

                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={14}>
                              {timeStr}
                            </text>
                            <text x={0} y={0} dy={32} textAnchor="middle" fill="#666" fontSize={14}>
                              {dateStr}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis tick={{ fontSize: 14 }} width={25} />
                    <Tooltip 
                      content={<CustomTooltip />}
                      cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '5 5' }}
                      animationDuration={150}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    {seriesVisibility.generalPrice && (
                      <Line type="natural" dataKey="generalPrice" stroke="#ef4444" strokeWidth={2} name="General Price" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.feedInPrice && (
                      <Line type="natural" dataKey="feedInPrice" stroke="#eab308" strokeWidth={2} name="Feed-in Price" dot={false} animationDuration={0} />
                    )}
                    {pricingChartData.refLineTimestamp && pricingChartData.chartData.length > 0 && (
                      <ReferenceLine 
                        x={pricingChartData.refLineTimestamp} 
                        stroke="#DC2626" 
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        label={{
                            value: "Data Time",
                          position: "insideTopRight",
                          fill: "#DC2626",
                          fontSize: 14,
                          dy: 10
                        }}
                      />
                    )}
                    {/* Battery Control Schedule Windows */}
                    {batteryControlSchedules && pricingChartData.chartData && pricingChartData.chartData.length > 0 && 
                      batteryControlSchedules.map((schedule: BatteryControlSchedule, index: number) => {
                        try {
                          const isCharge = schedule.action === 'charge';
                          
                          return (
                            <ReferenceArea
                              key={`battery-schedule-${index}`}
                              x1={schedule.start}
                              x2={schedule.end}
                              fill={isCharge ? '#ec4899' : '#eab308'} // Pink for charge, yellow for discharge
                              fillOpacity={0.3}
                              stroke="none"
                              label={{
                                value: `${schedule.action.toUpperCase()}`,
                                position: 'center',
                                fill: isCharge ? '#be185d' : '#a16207',
                                fontSize: 10,
                                fontWeight: 'bold',
                                angle: -90,
                                offset: 0
                              }}
                            />
                          );
                        } catch (error) {
                          console.error(`Error rendering battery schedule window ${index}:`, error, schedule);
                          return null;
                        }
                      })
                    }
                    {/* Low Price Windows */}
                    {pricingChartData.lowPeriods && pricingChartData.lowPeriods.length > 0 && pricingChartData.chartData && pricingChartData.chartData.length > 0 && 
                       pricingChartData.lowPeriods.map((period, index) => {
                        try {
                          // Convert UTC timestamps to local time to match chart data timezone
                          const startTime = new Date(period.start);
                          const endTime = new Date(period.end);
                          
                          // Format to match the chart data time format (YYYY-MM-DDTHH:mm:ss+10:00)
                          const formatToLocalTime = (date: Date) => {
                            return formatInTimeZone(date, userTimezone, 'yyyy-MM-dd\'T\'HH:mm:ssxxx');
                          };
                          
                          return (
                            <ReferenceArea
                              key={`low-price-${index}`}
                              x1={formatToLocalTime(startTime)}
                              x2={formatToLocalTime(endTime)}
                              fill="#10b981"
                              fillOpacity={0.1}
                              stroke="none"
                              label={{
                                value: "Low Price",
                                position: "bottom",
                                fill: "#10b981",
                                fontSize: 10,
                                fontWeight: 'bold',
                                angle: 0,
                                offset: -20
                              }}
                            />
                          );
                        } catch (error) {
                          return null;
                        }
                      })
                    }

                  </LineChart>
                </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-center">
                  <p className="text-gray-500">No pricing data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Generation Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-1 lg:grid-cols-1ap-6">
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Power</h2>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => toggleSeries('fromGen')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.fromGen 
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.fromGen ? '#eab308' : '#d1d5db' }}></div>
                  <span>Solar Generation</span>
                </button>
                <button
                  onClick={() => toggleSeries('energyGridPlus')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.energyGridPlus 
                      ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.energyGridPlus ? '#6b7280' : '#d1d5db' }}></div>
                  <span>From Grid</span>
                </button>
                <button
                  onClick={() => toggleSeries('energyGridMinus')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.energyGridMinus 
                      ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.energyGridMinus ? '#3b82f6' : '#d1d5db' }}></div>
                  <span>To Grid</span>
                </button>
              </div>

               {generationChartData.chartData && generationChartData.chartData.length > 0 ? (
                <div ref={chartRef} onTouchStart={handleChartTouch}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={generationChartData.chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="time" 
                        height={60}
                        interval={getInterval(chartWidth)}
                        tick={(props) => {
                          const { x, y, payload } = props;
                          const date = new Date(payload.value);
                          
                          // Format the date and time
                          const timeStr = date.toLocaleTimeString(userLocale, {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          });
                          const dateStr = date.toLocaleDateString(userLocale, {
                            day: 'numeric',
                            month: 'numeric',
                          });

                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={14}>
                              {timeStr}
                            </text>
                            <text x={0} y={0} dy={32} textAnchor="middle" fill="#666" fontSize={14}>
                              {dateStr}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis tick={{ fontSize: 14 }} width={25} />
                    <Tooltip 
                      content={<CustomTooltip />}
                      cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '5 5' }}
                      animationDuration={150}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    {seriesVisibility.fromGen && (
                      <Line type="monotone" dataKey="fromGen" stroke="#eab308" strokeWidth={2} name="Solar Generation" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.energyGridPlus && (
                      <Line type="monotone" dataKey="energyGridPlus" stroke="#6b7280" strokeWidth={2} name="Grid Import" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.energyGridMinus && (
                      <Line type="monotone" dataKey="energyGridMinus" stroke="#3b82f6" strokeWidth={2} name="Grid Export" dot={false} animationDuration={0} />
                    )}
                    {generationChartData.refLineTimestamp && generationChartData.chartData.length > 0 && (
                      <ReferenceLine 
                        x={generationChartData.refLineTimestamp} 
                        stroke="#DC2626" 
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        label={{
                          value: "Data Time",
                          position: "insideTopRight",
                          fill: "#DC2626",
                          fontSize: 14,
                          dy: 10
                        }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-center">
                  <p className="text-gray-500">No generation data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Consumption Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-1 lg:grid-cols-1ap-6">
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Consumption</h2>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => toggleSeries('fromBattToConsumer')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.fromBattToConsumer 
                      ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.fromBattToConsumer ? '#3b82f6' : '#d1d5db' }}></div>
                  <span>From Battery to Consumer</span>
                </button>
                <button
                  onClick={() => toggleSeries('fromGenToConsumer')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.fromGenToConsumer 
                      ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.fromGenToConsumer ? '#10b981' : '#d1d5db' }}></div>
                  <span>From PV to Consumer</span>
                </button>
                <button
                  onClick={() => toggleSeries('fromGridToConsumer')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.fromGridToConsumer 
                      ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.fromGridToConsumer ? '#f59e0b' : '#d1d5db' }}></div>
                  <span>From Grid to Consumer</span>
                </button>
                <button
                  onClick={() => toggleSeries('toConsumer')}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium transition-none ${
                    seriesVisibility.toConsumer 
                      ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' 
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-2.5 h-2.5 rounded-full transition-none" style={{ backgroundColor: seriesVisibility.toConsumer ? '#6b7280' : '#d1d5db' }}></div>
                  <span>Total Consumption</span>
                </button>
              </div>

                            {consumptionChartData.chartData && consumptionChartData.chartData.length > 0 ? (
                <div ref={chartRef} onTouchStart={handleChartTouch}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={consumptionChartData.chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="time" 
                        height={60}
                        interval={getInterval(chartWidth)}
                        tick={(props) => {
                          const { x, y, payload } = props;
                          const date = new Date(payload.value);
                          
                          // Format the date and time
                          const timeStr = date.toLocaleTimeString(userLocale, {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          });
                          const dateStr = date.toLocaleDateString(userLocale, {
                            day: 'numeric',
                            month: 'numeric',
                          });

                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={14}>
                              {timeStr}
                            </text>
                            <text x={0} y={0} dy={32} textAnchor="middle" fill="#666" fontSize={14}>
                              {dateStr}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis tick={{ fontSize: 14 }} width={25} />
                    <Tooltip 
                      content={<CustomTooltip />}
                      cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '5 5' }}
                      animationDuration={150}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    {seriesVisibility.toConsumer && (
                      <Line type="monotone" dataKey="toConsumer" stroke="#9ca3af" strokeWidth={2} name="Total Consumption" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.fromBattToConsumer && (
                      <Line type="monotone" dataKey="fromBattToConsumer" stroke="#3b82f6" strokeWidth={2} name="From Battery" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.fromGenToConsumer && (
                      <Line type="monotone" dataKey="fromGenToConsumer" stroke="#10b981" strokeWidth={2} name="From Solar" dot={false} animationDuration={0} />
                    )}
                    {seriesVisibility.fromGridToConsumer && (
                      <Line type="monotone" dataKey="fromGridToConsumer" stroke="#f59e0b" strokeWidth={2} name="From Grid" dot={false} animationDuration={0} />
                    )}
                    {consumptionChartData.refLineTimestamp && consumptionChartData.chartData.length > 0 && (
                      <ReferenceLine 
                        x={consumptionChartData.refLineTimestamp} 
                        stroke="#DC2626" 
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        label={{
                          value: "Data Time",
                          position: "insideTopRight",
                          fill: "#DC2626",
                          fontSize: 14,
                          dy: 10
                        }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-center">
                  <p className="text-gray-500">No consumption data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Battery Control Schedules Tile - Right Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-1 2xl:col-span-1 gap-6">
          {/* Grid Export Control Status */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Grid Export Control</h2>
              <div className="flex items-center space-x-2">
                {gridExportStatus?.enabled && gridExportStatus?.scheduler_running ? (
                  <span className="flex items-center text-green-600 text-sm">
                    <span className="w-2 h-2 bg-green-600 rounded-full mr-2 animate-pulse"></span>
                    Active
                  </span>
                ) : gridExportStatus?.enabled ? (
                  <span className="text-gray-500 text-sm">Inactive</span>
                ) : (
                  <span className="text-gray-400 text-sm">Disabled</span>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              {/* Power Limit Status */}
              {gridExportStatus?.status?.grid_export_enabled !== undefined && (
                <div className="flex justify-between items-center p-3 rounded-lg bg-gray-50">
                  <span className="text-sm text-gray-700">Grid Export</span>
                  <span className={`text-sm font-medium ${gridExportStatus.status.grid_export_enabled ? 'text-green-600' : 'text-amber-600'}`}>
                    {gridExportStatus.status.grid_export_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              )}

              {/* Feed-in Tarrif */}
              {gridExportStatus?.status?.feed_in_tariff !== undefined && (
                <div className="flex justify-between items-center p-3 rounded-lg bg-gray-50">
                  <span className="text-sm text-gray-700">Feed-In Tariff</span>
                  <span className={`text-sm font-medium ${gridExportStatus.status.feed_in_tariff < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {gridExportStatus.status.feed_in_tariff.toFixed(2)} c/kWh
                  </span>
                </div>
              )}

              {/* Manual Trigger Button - Development Only */}
              {process.env.NODE_ENV === 'development' && (
                <div className="pt-2 border-t">
                  <button
                    onClick={async () => {
                      try {
                        await apiService.triggerGridExportControl();
                        toast.success('Grid export control triggered manually');
                        // Refresh the grid export status
                        refetchGridExport();
                      } catch (error) {
                        toast.error('Failed to trigger grid export control');
                        console.error('Error triggering grid export control:', error);
                      }
                    }}
                    className="w-full px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    🔧 Trigger Manually (Dev)
                  </button>
                </div>
              )}

              {/* Last Run */}
              {gridExportStatus?.status?.last_run && (
                <div className="text-xs text-gray-500 text-center pt-2 border-t">
                  Last checked: {format(parseISO(gridExportStatus.status.last_run), 'HH:mm:ss')}
                </div>
              )}
              
              {/* Error Message */}
              {gridExportStatus?.status?.error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <span className="text-xs text-red-700">{gridExportStatus.status.error}</span>
                </div>
              )}
            </div>

          </div>
          
          <BatteryControlSchedulesTile
            schedules={batteryControlSchedules || []}
            recommendations={batteryRecommendations || []}
            lastUpdated={selectedEntry?.created_at || null}
            dataId={selectedDataId}
            isLoading={batterySchedulesLoading || batteryRecommendationsLoading}
            onSchedulesChange={() => {
              refetchBatterySchedules();
              refetchBatteryRecommendations();
            }}
          />
          
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
